import { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldL:"#d4a853", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  orange:"#d97706", orangeL:"rgba(217,119,6,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const EXCLUDED_ROLES = ["admin", "director"];
const PERIOD_ANCHOR = "2026-05-04"; // Mon May 4 2026 — first biweekly period start
const PERIOD_DAYS = 14;

function getTodayPT() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
}

function getPeriodForDate(dateStr) {
  const anchor = new Date(PERIOD_ANCHOR + "T00:00:00Z");
  const target = new Date(dateStr + "T00:00:00Z");
  const diffDays = Math.floor((target - anchor) / 86400000);
  const idx = Math.floor(diffDays / PERIOD_DAYS);
  const start = new Date(anchor);
  start.setUTCDate(anchor.getUTCDate() + idx * PERIOD_DAYS);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + PERIOD_DAYS - 1);
  return {
    idx,
    start: start.toISOString().split("T")[0],
    end: end.toISOString().split("T")[0],
  };
}

function getCurrentPeriod() {
  return getPeriodForDate(getTodayPT());
}

function formatPeriodLabel(p) {
  const s = new Date(p.start + "T00:00:00Z");
  const e = new Date(p.end + "T00:00:00Z");
  const fmt = d => d.toLocaleDateString("en-US", { month:"short", day:"numeric", timeZone:"UTC" });
  return `${fmt(s)} – ${fmt(e)}, ${e.getUTCFullYear()}`;
}

function formatTimePT(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true, timeZone:"America/Los_Angeles" });
}

export default function PayrollPage() {
  const [period, setPeriod] = useState(getCurrentPeriod());
  const [profiles, setProfiles] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const [tipAppts, setTipAppts] = useState([]);
  const [providers, setProviders] = useState([]);

  const load = async () => {
    setLoading(true);
    const { data: profs } = await supabase.from("profiles").select("*");
    setProfiles((profs || []).filter(p => !EXCLUDED_ROLES.includes(p.role)));

    const { data: sh } = await supabase.from("time_clock")
      .select("*").gte("shift_date", period.start).lte("shift_date", period.end)
      .order("shift_date", { ascending: false });
    setShifts(sh || []);

    const { data: al } = await supabase.from("timeclock_alerts")
      .select("*").gte("shift_date", period.start).lte("shift_date", period.end);
    setAlerts(al || []);

    // ★ Tips — the POS records tips on PAYMENTS, not on appointments.
    //   Each payment row may have metadata = { tip: <number>, provider_id: <int>, ... }
    //   when the cashier added a tip at checkout. We pull every payment in
    //   the period whose metadata.tip is non-zero, then normalise them into
    //   the same shape the rest of this page expects (tip, prov, pid, cn,
    //   treat, date, id) so the aggregation downstream "just works".
    //
    //   Note: querying jsonb with `.not is null` on a nested key isn't a
    //   first-class Supabase REST filter — we pull payments in the period
    //   and filter client-side. The payment volume per period is small
    //   (hundreds, not millions) so this is fine.
    const periodStartISO = period.start + "T00:00:00";
    const periodEndISO   = period.end   + "T23:59:59";
    const { data: paymentsForTips } = await supabase.from("payments")
      .select("id,client_id,client_name,description,appointment_id,created_at,metadata,status")
      .gte("created_at", periodStartISO).lte("created_at", periodEndISO)
      .order("created_at", { ascending: false });

    // Pull a quick map of providers so we can resolve metadata.provider_id → name
    const { data: provs } = await supabase.from("providers").select("id,name,user_id");
    const provByIdQuick = new Map((provs || []).map(p => [p.id, p]));

    const tips = (paymentsForTips || [])
      .map(p => {
        const meta = p.metadata || {};
        const tipAmount = +(meta.tip || 0);
        if (!tipAmount || tipAmount <= 0) return null;
        // Refunded payments shouldn't count towards earnings
        if (p.status === "refunded" || p.status === "voided") return null;
        const pid = +(meta.provider_id || 0) || null;
        const provName = pid ? (provByIdQuick.get(pid)?.name || "") : "";
        return {
          id: p.id,
          cn: p.client_name || "",
          treat: p.description || "Payment",
          date: (p.created_at || "").slice(0, 10),
          time: (p.created_at || "").slice(11, 16),
          prov: provName,
          pid,
          tip: tipAmount,
          paid: true,
          status: p.status || "succeeded",
        };
      })
      .filter(Boolean);
    setTipAppts(tips);
    // We fetched `provs` above to resolve provider_id → name on tips;
    // reuse the same list for the state instead of re-querying.
    setProviders(provs || []);

    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase.channel("payroll-page-realtime")
      .on("postgres_changes", { event:"*", schema:"public", table:"time_clock" }, load)
      .on("postgres_changes", { event:"*", schema:"public", table:"timeclock_alerts" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.idx]);

  const byEmployee = useMemo(() => {
    const map = {};
    profiles.forEach(p => {
      map[p.id] = { user:p, shifts:[], tips:[], totalHours:0, totalEarnings:0, totalTips:0, openShifts:0, alertCount:0 };
    });
    shifts.forEach(s => {
      if (!map[s.user_id]) {
        // Shifts from deleted/excluded users — show under "Unknown"
        map[s.user_id] = { user:{ id:s.user_id, full_name:s.user_name||"Unknown", email:"", role:"" }, shifts:[], tips:[], totalHours:0, totalEarnings:0, totalTips:0, openShifts:0, alertCount:0 };
      }
      map[s.user_id].shifts.push(s);
      map[s.user_id].totalHours += parseFloat(s.hours_worked || 0);
      map[s.user_id].totalEarnings += parseFloat(s.earnings || 0);
      if (!s.check_out_at) map[s.user_id].openShifts++;
    });
    alerts.forEach(a => {
      if (a.status !== "open") return;
      if (map[a.user_id]) map[a.user_id].alertCount++;
    });

    // ★ Match tip appointments to profiles via providers.user_id OR by name
    const provById = new Map(providers.map(p => [p.id, p]));
    const profByName = new Map(profiles.map(p => [(p.full_name || "").toLowerCase().trim(), p]));
    tipAppts.forEach(a => {
      let userId = null;
      // Try by provider_id → providers.user_id
      const prov = provById.get(a.pid);
      if (prov?.user_id) userId = prov.user_id;
      // Fallback: match provider name to profile name
      if (!userId && a.prov) {
        const match = profByName.get(a.prov.toLowerCase().trim());
        if (match) userId = match.id;
      }
      if (!userId) {
        // Tip with unknown provider — bucket under "Unknown"
        userId = "_unknown_tips";
        if (!map[userId]) {
          map[userId] = { user:{ id:userId, full_name:`Unknown (${a.prov || "no provider"})`, email:"", role:"" }, shifts:[], tips:[], totalHours:0, totalEarnings:0, totalTips:0, openShifts:0, alertCount:0 };
        }
      }
      if (!map[userId]) return;
      map[userId].tips.push(a);
      map[userId].totalTips += parseFloat(a.tip || 0);
    });

    return Object.values(map).sort((a,b) => ((b.totalEarnings + b.totalTips) - (a.totalEarnings + a.totalTips)) || a.user.full_name?.localeCompare(b.user.full_name||""));
  }, [profiles, shifts, alerts, tipAppts, providers]);

  const totals = useMemo(() => byEmployee.reduce((a, e) => ({
    hours: a.hours + e.totalHours,
    earnings: a.earnings + e.totalEarnings,
    tips: a.tips + e.totalTips,
    workers: a.workers + (e.shifts.length > 0 || e.tips.length > 0 ? 1 : 0),
    alerts: a.alerts + e.alertCount + e.openShifts,
  }), { hours:0, earnings:0, tips:0, workers:0, alerts:0 }), [byEmployee]);

  const goPrev = () => {
    const s = new Date(period.start + "T00:00:00Z");
    s.setUTCDate(s.getUTCDate() - PERIOD_DAYS);
    setPeriod(getPeriodForDate(s.toISOString().split("T")[0]));
  };
  const goNext = () => {
    const s = new Date(period.start + "T00:00:00Z");
    s.setUTCDate(s.getUTCDate() + PERIOD_DAYS);
    setPeriod(getPeriodForDate(s.toISOString().split("T")[0]));
  };
  const goCurrent = () => setPeriod(getCurrentPeriod());

  const exportCSV = () => {
    const rows = [["Employee","Hours","Hourly Earnings","Tips","Total Pay"]];
    byEmployee.filter(e => e.totalHours > 0 || e.totalTips > 0).forEach(e => {
      rows.push([
        (e.user.full_name || e.user.email || "Unknown"),
        e.totalHours.toFixed(2),
        e.totalEarnings.toFixed(2),
        e.totalTips.toFixed(2),
        (e.totalEarnings + e.totalTips).toFixed(2),
      ]);
    });
    rows.push(["TOTAL", totals.hours.toFixed(2), totals.earnings.toFixed(2), totals.tips.toFixed(2), (totals.earnings + totals.tips).toFixed(2)]);
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll_${period.start}_${period.end}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const isCurrentPeriod = period.idx === getCurrentPeriod().idx;

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
      {/* Period selector + Export */}
      <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10 }}>
        <div style={{ display:"flex",alignItems:"center",gap:10,flexWrap:"wrap" }}>
          <button onClick={goPrev} style={{ padding:"8px 14px",background:C.gold,color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:14 }}>←</button>
          <div>
            <div style={{ fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em" }}>Pay Period {isCurrentPeriod && <span style={{ color:C.green }}>· CURRENT</span>}</div>
            <div style={{ fontSize:16,fontWeight:800,color:C.gold,fontFamily:S }}>{formatPeriodLabel(period)}</div>
          </div>
          <button onClick={goNext} style={{ padding:"8px 14px",background:C.gold,color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:14 }}>→</button>
          {!isCurrentPeriod && <button onClick={goCurrent} style={{ padding:"6px 12px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:7,cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:F }}>Today</button>}
        </div>
        <button onClick={exportCSV} disabled={totals.hours === 0} style={{ padding:"10px 18px",background:totals.hours===0?C.light:`linear-gradient(135deg,${C.green},#1e6b3f)`,color:totals.hours===0?C.muted:"#fff",border:totals.hours===0?`1px solid ${C.border}`:"none",borderRadius:8,cursor:totals.hours===0?"not-allowed":"pointer",fontSize:13,fontWeight:700,fontFamily:F }}>📥 Export CSV</button>
      </div>

      {/* Stats */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12 }}>
        <Stat label="Active Workers" value={totals.workers} color={C.blue} icon="👥" />
        <Stat label="Total Hours" value={totals.hours.toFixed(1)} color={C.gold} icon="⏱️" />
        <Stat label="Hourly Pay" value={`$${totals.earnings.toFixed(2)}`} color={C.green} icon="💰" />
        <Stat label="Tips" value={`$${totals.tips.toFixed(2)}`} color={C.orange} icon="💸" />
        <Stat label="Total Payroll" value={`$${(totals.earnings + totals.tips).toFixed(2)}`} color={C.green} icon="✨" />
        <Stat label="Issues" value={totals.alerts} color={totals.alerts > 0 ? C.red : C.muted} icon={totals.alerts > 0 ? "⚠️" : "✓"} />
      </div>

      {/* Employee breakdown */}
      <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden" }}>
        <div style={{ padding:"14px 18px",borderBottom:`1px solid ${C.border}`,background:C.goldBg,fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,display:"flex",justifyContent:"space-between" }}>
          <span>Employee Breakdown</span>
          <span style={{ fontSize:11,color:C.muted,fontFamily:F,fontWeight:600 }}>Click row to see all shifts</span>
        </div>
        {loading ? (
          <div style={{ padding:40,textAlign:"center",color:C.muted,fontSize:13 }}>Loading…</div>
        ) : byEmployee.length === 0 ? (
          <div style={{ padding:40,textAlign:"center",color:C.muted,fontSize:13 }}>No employees. Add staff and rates in Users & Roles → Pay & Schedule.</div>
        ) : byEmployee.filter(e => e.shifts.length > 0 || e.tips.length > 0 || (e.user.hourly_rate || 0) > 0).map(emp => (
          <EmployeeRow
            key={emp.user.id}
            emp={emp}
            expanded={expanded === emp.user.id}
            onToggle={() => setExpanded(expanded === emp.user.id ? null : emp.user.id)}
            onEditShift={s => setEditing({ shift:s, employee:emp.user })}
          />
        ))}
      </div>

      {editing && (
        <EditShiftModal
          shift={editing.shift}
          employee={editing.employee}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, color, icon }) {
  return (
    <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 18px" }}>
      <div style={{ fontSize:18,marginBottom:8 }}>{icon}</div>
      <div style={{ fontSize:24,fontWeight:800,color,fontFamily:S,marginBottom:2 }}>{value}</div>
      <div style={{ fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em" }}>{label}</div>
    </div>
  );
}

function EmployeeRow({ emp, expanded, onToggle, onEditShift }) {
  const rate = parseFloat(emp.user.hourly_rate || 0);
  const grandTotal = emp.totalEarnings + emp.totalTips;
  return (
    <>
      <div onClick={onToggle} style={{ padding:"14px 18px",borderBottom:expanded?"none":`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:14,cursor:"pointer",background:expanded?C.goldBg:"transparent",transition:"background 0.15s" }}>
        <div style={{ width:40,height:40,borderRadius:10,background:C.goldBg,border:`1px solid ${C.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:C.gold,flexShrink:0 }}>
          {(emp.user.full_name || emp.user.email || "?").charAt(0).toUpperCase()}
        </div>
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ fontSize:14,fontWeight:700,color:C.text }}>{emp.user.full_name || emp.user.email || "Unknown"}</div>
          <div style={{ fontSize:11,color:C.muted,marginTop:2,display:"flex",flexWrap:"wrap",gap:6,alignItems:"center" }}>
            <span>{emp.shifts.length} shift{emp.shifts.length===1?"":"s"} · ${rate.toFixed(2)}/hr</span>
            {emp.tips.length > 0 && <span style={{ color:C.orange,fontWeight:700 }}>· 💸 {emp.tips.length} tip{emp.tips.length>1?"s":""}</span>}
            {emp.openShifts > 0 && <span style={{ color:C.red,fontWeight:700 }}>· {emp.openShifts} OPEN</span>}
            {emp.alertCount > 0 && <span style={{ color:C.red,fontWeight:700 }}>· ⚠️ {emp.alertCount} alert{emp.alertCount>1?"s":""}</span>}
          </div>
        </div>
        <div style={{ textAlign:"right",flexShrink:0,display:"flex",gap:14,alignItems:"center" }}>
          {emp.totalTips > 0 && (
            <div>
              <div style={{ fontSize:15,fontWeight:800,color:C.orange,fontFamily:S }}>${emp.totalTips.toFixed(2)}</div>
              <div style={{ fontSize:10,color:C.muted }}>tips</div>
            </div>
          )}
          <div>
            <div style={{ fontSize:18,fontWeight:800,color:C.gold,fontFamily:S }}>${grandTotal.toFixed(2)}</div>
            <div style={{ fontSize:11,color:C.muted }}>{emp.totalHours.toFixed(1)} hrs total</div>
          </div>
        </div>
        <div style={{ color:C.muted,fontSize:14,width:14,textAlign:"center" }}>{expanded ? "▾" : "▸"}</div>
      </div>
      {expanded && (
        <div style={{ borderBottom:`1px solid ${C.border}`,background:"#fafaf8",padding:"6px 18px 14px 72px" }}>
          {/* Shifts section */}
          <div style={{ fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.06em",margin:"10px 0 6px" }}>⏱ Shifts ({emp.shifts.length})</div>
          {emp.shifts.length === 0 ? (
            <div style={{ padding:8,fontSize:12,color:C.muted,fontStyle:"italic" }}>No shifts in this period.</div>
          ) : emp.shifts.map(s => <ShiftRow key={s.id} shift={s} onEdit={() => onEditShift(s)} />)}

          {/* Tips section */}
          {emp.tips.length > 0 && (
            <>
              <div style={{ fontSize:11,fontWeight:700,color:C.orange,textTransform:"uppercase",letterSpacing:"0.06em",margin:"14px 0 6px" }}>💸 Tips Received ({emp.tips.length} · ${emp.totalTips.toFixed(2)} total)</div>
              {emp.tips.map(t => (
                <div key={t.id} style={{ padding:"8px 14px",margin:"4px 0",background:"#fff",border:`1px solid ${C.orange}30`,borderLeft:`3px solid ${C.orange}`,borderRadius:8,display:"flex",alignItems:"center",gap:12,fontSize:12 }}>
                  <div style={{ width:90,fontWeight:700,color:C.text,flexShrink:0 }}>{t.date}</div>
                  <div style={{ flex:1,color:C.muted,minWidth:0 }}>
                    {t.time} · {t.cn} · <strong style={{ color:C.text }}>{t.treat}</strong>
                  </div>
                  <div style={{ fontWeight:800,color:C.orange,minWidth:60,textAlign:"right",fontSize:14 }}>${Number(t.tip).toFixed(2)}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}

function ShiftRow({ shift, onEdit }) {
  const isOpen = !shift.check_out_at;
  const isEdited = shift.status === "manual_edit" || shift.edited_at;
  return (
    <div style={{ padding:"10px 14px",margin:"6px 0",background:"#fff",border:`1px solid ${isOpen?C.red+"60":C.border}`,borderRadius:8,display:"flex",alignItems:"center",gap:12,fontSize:12 }}>
      <div style={{ width:90,fontWeight:700,color:C.text,flexShrink:0 }}>{shift.shift_date}</div>
      <div style={{ flex:1,color:C.muted,minWidth:0 }}>
        In: <strong style={{ color:C.text }}>{formatTimePT(shift.check_in_at)}</strong>
        {" · "}
        Out: <strong style={{ color:isOpen?C.red:C.text }}>{isOpen ? "OPEN" : formatTimePT(shift.check_out_at)}</strong>
        {isEdited && <span style={{ marginLeft:8,fontSize:10,padding:"1px 6px",background:C.blueL,color:C.blue,borderRadius:6,fontWeight:700 }}>EDITED</span>}
      </div>
      <div style={{ fontWeight:700,color:isOpen?C.muted:C.text,minWidth:40,textAlign:"right" }}>{shift.hours_worked || 0}h</div>
      <div style={{ fontWeight:700,color:C.gold,minWidth:60,textAlign:"right" }}>${shift.earnings || 0}</div>
      <button onClick={onEdit} style={{ padding:"4px 10px",fontSize:11,fontWeight:600,background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:6,cursor:"pointer",fontFamily:F }}>Edit</button>
    </div>
  );
}

// Convert ISO timestamp <-> "YYYY-MM-DDTHH:MM" in PT for datetime-local input
function isoToLocalPT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone:"America/Los_Angeles",
    year:"numeric", month:"2-digit", day:"2-digit",
    hour:"2-digit", minute:"2-digit", hour12:false,
  });
  const parts = {};
  fmt.formatToParts(d).forEach(p => parts[p.type] = p.value);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}`;
}

function localPTToIso(local) {
  if (!local) return null;
  // Interpret 'local' as PT wall-clock time, return UTC ISO
  const [datePart, timePart] = local.split("T");
  const [y,m,d] = datePart.split("-").map(Number);
  const [hh,mm] = timePart.split(":").map(Number);
  // Brute-force: try a UTC time, see what PT it shows, adjust
  let candidate = new Date(Date.UTC(y, m-1, d, hh, mm));
  for (let i = 0; i < 3; i++) {
    const ptStr = new Intl.DateTimeFormat("en-CA", { timeZone:"America/Los_Angeles", year:"numeric", month:"2-digit", day:"2-digit", hour:"2-digit", minute:"2-digit", hour12:false }).formatToParts(candidate);
    const p = {};
    ptStr.forEach(x => p[x.type] = x.value);
    const driftMin = ((y - +p.year) * 60*24*365) + ((m - +p.month) * 60*24*30) + ((d - +p.day) * 60*24) + ((hh - (+p.hour === 24 ? 0 : +p.hour)) * 60) + (mm - +p.minute);
    if (driftMin === 0) break;
    candidate = new Date(candidate.getTime() + driftMin * 60000);
  }
  return candidate.toISOString();
}

function EditShiftModal({ shift, employee, onClose, onSaved }) {
  const [checkIn, setCheckIn] = useState(isoToLocalPT(shift.check_in_at));
  const [checkOut, setCheckOut] = useState(isoToLocalPT(shift.check_out_at));
  const [reason, setReason] = useState(shift.edit_reason || "");
  const [saving, setSaving] = useState(false);

  const calc = () => {
    if (!checkIn || !checkOut) return { hours:0, earnings:0 };
    const inIso = localPTToIso(checkIn);
    const outIso = localPTToIso(checkOut);
    const ms = new Date(outIso).getTime() - new Date(inIso).getTime();
    if (ms <= 0) return { hours:0, earnings:0 };
    const hours = +(ms / 3600000).toFixed(2);
    const rate = parseFloat(shift.hourly_rate || employee?.hourly_rate || 0);
    return { hours, earnings: +(hours * rate).toFixed(2) };
  };
  const { hours, earnings } = calc();

  const save = async () => {
    if (!checkIn) { window.alert("Check-in time required"); return; }
    setSaving(true);
    const inIso = localPTToIso(checkIn);
    const outIso = checkOut ? localPTToIso(checkOut) : null;
    const updates = {
      check_in_at: inIso,
      check_out_at: outIso,
      hours_worked: outIso ? hours : null,
      earnings: outIso ? earnings : null,
      status: outIso ? "manual_edit" : "active",
      edit_reason: reason || null,
      edited_at: new Date().toISOString(),
    };
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) updates.edited_by = user.id;
    } catch(e) { /* ignore */ }

    const { error } = await supabase.from("time_clock").update(updates).eq("id", shift.id);

    // Resolve any open alerts for this shift
    if (outIso) {
      await supabase.from("timeclock_alerts").update({ status:"resolved", resolved_at:new Date().toISOString() }).eq("shift_id", shift.id).eq("status","open");
    }

    setSaving(false);
    if (error) { window.alert("Save failed: " + error.message); return; }
    onSaved();
  };

  const removeShift = async () => {
    if (!window.confirm("Delete this shift entirely? This cannot be undone.")) return;
    setSaving(true);
    const { error } = await supabase.from("time_clock").delete().eq("id", shift.id);
    setSaving(false);
    if (error) { window.alert("Delete failed: " + error.message); return; }
    onSaved();
  };

  const inpStyle = { width:"100%",padding:"10px 12px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:F,boxSizing:"border-box",color:C.text,outline:"none" };

  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1500,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:C.surface,borderRadius:16,maxWidth:540,width:"100%",maxHeight:"92vh",overflow:"auto",padding:24 }}>
        <div style={{ fontSize:18,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4 }}>Edit Shift</div>
        <div style={{ fontSize:12,color:C.muted,marginBottom:20 }}>{employee?.full_name || shift.user_name || "Unknown"} · {shift.shift_date}</div>

        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14 }}>
          <div>
            <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6 }}>Check In (PT)</label>
            <input type="datetime-local" value={checkIn} onChange={e => setCheckIn(e.target.value)} style={inpStyle} />
          </div>
          <div>
            <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6 }}>Check Out (PT)</label>
            <input type="datetime-local" value={checkOut} onChange={e => setCheckOut(e.target.value)} style={inpStyle} />
          </div>
        </div>

        <div style={{ padding:"14px 16px",background:C.goldBg,borderRadius:10,marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div>
            <div style={{ fontSize:11,color:C.muted,fontWeight:600 }}>Hours: <strong style={{ color:C.text }}>{hours}</strong></div>
            <div style={{ fontSize:11,color:C.muted,fontWeight:600 }}>Rate: ${shift.hourly_rate || 0}/hr</div>
          </div>
          <div style={{ fontSize:24,fontWeight:800,color:C.gold,fontFamily:S }}>${earnings}</div>
        </div>

        <div style={{ marginBottom:18 }}>
          <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6 }}>Reason (optional)</label>
          <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Forgot to check out" style={inpStyle} />
        </div>

        <div style={{ display:"flex",gap:10,justifyContent:"space-between" }}>
          <button onClick={removeShift} disabled={saving} style={{ padding:"10px 14px",fontSize:12,fontWeight:600,background:C.redL,color:C.red,border:`1px solid ${C.red}40`,borderRadius:8,cursor:saving?"not-allowed":"pointer",fontFamily:F }}>🗑️ Delete</button>
          <div style={{ display:"flex",gap:10 }}>
            <button onClick={onClose} disabled={saving} style={{ padding:"10px 16px",fontSize:13,fontWeight:600,background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,cursor:saving?"not-allowed":"pointer",fontFamily:F }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ padding:"10px 20px",fontSize:13,fontWeight:700,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,cursor:saving?"not-allowed":"pointer",fontFamily:F,opacity:saving?0.6:1 }}>{saving?"Saving…":"💾 Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
